import { useCallback, useEffect, useState } from "react";
import { Button, Header, Image, Modal, ModalContent } from "semantic-ui-react";

// Components
import CardSaveServiceOrder from "react-lib/apps/MEH/CardSaveServiceOrder";
import CardRegisterService from "react-lib/apps/MEH/CardRegisterService";
import CardMedicalFee from "react-lib/apps/HISV3/BIL/CardMedicalFee";
import CardPaymentSummary from "react-lib/apps/MEH/CardPaymentSummary";
import CardPaymentHistory from "react-lib/apps/MEH/CardPaymentHistory";
import CardMePackageOrder from "react-lib/apps/MEH/CardMePackageOrder";
import CardMeLabOrder from "react-lib/apps/MEH/CardMeLabOrder";
import CardMeLabResult from "react-lib/apps/MEH/CardMeLabResult";
import CardPrintList from "react-lib/apps/common/CardPrintList";

// Serializers
import EmployeeSerializer from "issara-sdk/types/EmployeeSerializer_users";
import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";
import DoctorSerializer from "issara-sdk/types/DoctorSerializer_core";
import CentralLabOrderSimplifiedSerializer from "issara-sdk/types/CentralLabOrderSimplifiedSerializer_apps_LAB";
import SpecimenContainerSerializer from "issara-sdk/types/SpecimenContainerSerializer_apps_LAB";
import CentralLabOrderItemActionLogSerializer from "issara-sdk/types/CentralLabOrderItemActionLogSerializer_apps_LAB";
import VenipunctureNoteSerializer from "issara-sdk/types/VenipunctureNoteSerializer_apps_LAB";

// Config
import CONFIG from "config/config";

// Types
import {
  PickedState,
  SelectedCustomerData,
  VisitListData,
  AppointmentListData,
} from "react-lib/apps/MEH/types/MeBillingType";
import { SaveServiceOrderType } from "react-lib/apps/MEH/sequence/SaveServiceOrder";
import { MedicalFeeHistoryType } from "react-lib/apps/HISV3/BIL/sequence/MedicalFeeHistory";
import { PaymentSummaryType } from "react-lib/apps/MEH/sequence/PaymentSummary";
import { PaymentHistoryType } from "react-lib/apps/MEH/sequence/PaymentHistory";
import { State as PackagePurchaseState } from "react-lib/apps/HISV3/HCU/sequence/PackagePurchase";
import { State as PackageOrderState } from "react-lib/apps/HISV3/HCU/sequence/PackageOrder";
import { State as LabReportSequence } from "react-lib/apps/HISV3/LAB/sequence/LabReport";
import {
  LabOrderQueueType,
  SpecimenCollectorMasterDataType,
} from "react-lib/apps/HISV3/LAB/LabInterface";
import { State as OPDLabSummarySequence } from "react-lib/apps/HISV3/LAB/sequence/OPDLabSummary";
import { State as MakeAppointmentMeHeyState } from "react-lib/apps/MEH/sequence/MakeAppointmentMeHey";
import { State as SharedLabResultSequence } from "react-lib/apps/MEH/sequence/SharedLabResult";

export const MENU_KEYS = {
  REGISTER_SERVICE: "ลงทะเบียนรับบริการ",
  SAVE_SERVICE_ORDER: "บันทึกค่าบริการ",
  PENDING_PAYMENT: "รายการรอรับชำระ",
  PAYMENT_SUMMARY: "สรุปค่าบริการทั้งหมด",
  // ALL_DOCUMENTS: "เอกสารทั้งหมด",
  PAYMENT_HISTORY: "ประวัติชำระค่าบริการ",
  PACKAGE: "Package",
  PRODUCT_SERVICE: "เลือกสินค้าและบริการ",
  SPECIMEN_COLLECTION: "Specimen Collection",
  LAB_RESULT: "Lab Result",
  PRINT_LIST: "Print List",
} as const;

export type MenuKey = (typeof MENU_KEYS)[keyof typeof MENU_KEYS];

export const MENUS: MenuKey[] = [
  MENU_KEYS.REGISTER_SERVICE,
  MENU_KEYS.SPECIMEN_COLLECTION,
  MENU_KEYS.LAB_RESULT,
  MENU_KEYS.SAVE_SERVICE_ORDER,
  MENU_KEYS.PENDING_PAYMENT,
  MENU_KEYS.PAYMENT_SUMMARY,
  // MENU_KEYS.ALL_DOCUMENTS,
  MENU_KEYS.PAYMENT_HISTORY,
  MENU_KEYS.PACKAGE,
  MENU_KEYS.PRODUCT_SERVICE,
  MENU_KEYS.PRINT_LIST,
];

// แสดงเฉพาะ staff
export const STAFF_ONLY_MENUS: MenuKey[] = [MENU_KEYS.SPECIMEN_COLLECTION, MENU_KEYS.LAB_RESULT];

const IMAGES = {
  saveSuccess: "/static/images/meBilling/save_success.png",
};

export type CardMeBillingMenuContentProps = {
  menu: MenuKey;
  onSelectMenu: (menu: MenuKey) => void;

  setProp: any;
  runSequence: any;
  onEvent: any;
  controller: any;

  // data
  selectedCustomer: SelectedCustomerData | undefined;
  visitList: VisitListData | undefined;
  selectedVisit: EncounterSerializer | undefined;
  doctorList: DoctorSerializer[];
  appointmentList: AppointmentListData | undefined;
  providerList: EmployeeSerializer[];
  packageDetail: Record<string, any>[];

  // sequence
  SaveServiceOrderSequence?: SaveServiceOrderType;
  MedicalFeeHistorySequence?: MedicalFeeHistoryType;
  PaymentSummarySequence?: PaymentSummaryType;
  PaymentHistorySequence?: PaymentHistoryType;
  PackagePurchaseSequence?: PackagePurchaseState["PackagePurchaseSequence"];
  PackageOrderSequence?: PackageOrderState["PackageOrderSequence"];
  MakeAppointmentMeHeySequence?: MakeAppointmentMeHeyState["MakeAppointmentMeHeySequence"];
  // CommonInterface
  searchedItemListWithKey?: Record<string, any>;
  availableSlots?: Record<string, any>[];
  divisionId?: string;
  userTokenize?: Record<string, any>;
  languageUX: "en" | "th";

  // Patient
  patientInfo?: Record<string, any>;
  selectedEncounter?: Record<string, any>;
  django?: Record<string, any>;
  printListController?: Record<string, any>;

  // Invoice items
  invoiceItemByModes?: Record<string, any>[];
  invoiceItemByOrders?: Record<string, any>[];
  invoiceItemByItems?: Record<string, any>[];
  invoiceItemPackageList?: Record<string, any>[];

  // Orders
  patientOrders?: Record<string, any>[];
  refundOrders?: Record<string, any>;

  // Filters / Status
  medicalFeeFilter?: Record<string, any>;
  patientEncounterLockerStatus?: Record<string, any>[];
  encounterSuspension?: Record<string, any>;

  // Bill / Payment
  BillPaymentSequence?: Record<string, any> | null;
  billPendingDetail?: Record<string, any>;
  billReportPrint?: Record<string, any>;
  bilReceiptCodeDetail?: Record<string, any>;
  faxClaimItemList?: Record<string, any>;

  // Lab Order
  labOrderQueue?: LabOrderQueueType;
  selectedLabOrderWorking?: CentralLabOrderSimplifiedSerializer;
  specimenCollectorMasterData?: SpecimenCollectorMasterDataType;
  selectedContainer?: SpecimenContainerSerializer | null;
  specimenLogs?: CentralLabOrderItemActionLogSerializer[];
  selectedCentralLabBO?: Record<string, any>;

  // Lab Report
  LabReportSequence?: LabReportSequence["LabReportSequence"];
  permissionLabResultConfidential?: boolean;

  // Lab Summary
  OPDLabSummarySequence?: OPDLabSummarySequence["OPDLabSummarySequence"];
  selectedPatient?: Record<string, any>;
  centralLabResultList?: {
    items?: Record<string, any>[];
  };

  // Master / Task
  masterOptions?: Record<string, any[]>;
  masterData?: Record<string, any[]>;
  taskTrackingList?: Record<string, any>;
  divisionType?: string;

  // Language
  billingPatientLanguage?: any;

  // shared lab result
  SharedLabResultSequence?: SharedLabResultSequence["SharedLabResultSequence"];

  // CardVenipunctureNote
  selectedVenipunctureNote?: VenipunctureNoteSerializer;

  // config
  isMePOSCRM?: boolean;
} & Pick<
  PickedState,
  | "buttonLoadCheck"
  | "errorMessage"
  | "successMessage"
  | "loadingStatus"
  | "selectedDivision"
  | "qrCodePayment"
  | "userProfile"
>;

const CardMeBillingMenuContent = (props: CardMeBillingMenuContentProps) => {
  const [openSuccessMod, setOpenSuccessMod] = useState<boolean>(false);

  useEffect(() => {
    props.onEvent({
      message: "HandleGetReceiptCode",
      params: {},
    });
  }, []);

  const handleNavigateToSaveService = useCallback(() => {
    props.onSelectMenu(MENU_KEYS.SAVE_SERVICE_ORDER);
  }, [props.onSelectMenu]);

  const handleOpenEncounterAppointmentSuccess = useCallback(() => {
    setOpenSuccessMod(true);
  }, []);

  const renderPackageOrder = (showMedicalFee: boolean) => (
    <CardMePackageOrder
      setProp={props.setProp}
      onEvent={props.onEvent}
      controller={props.controller}
      // seq
      runSequence={props.runSequence}
      PackagePurchaseSequence={props.PackagePurchaseSequence}
      PackageOrderSequence={props.PackageOrderSequence}
      MakeAppointmentMeHeySequence={props.MakeAppointmentMeHeySequence}
      // Filter
      // CommonInterface
      selectedPatient={props.selectedPatient}
      searchedItemListWithKey={props.searchedItemListWithKey}
      buttonLoadCheck={props.buttonLoadCheck}
      errorMessage={props.errorMessage}
      successMessage={props.successMessage}
      // options
      masterData={props.masterData}
      masterOptions={props.masterOptions}
      languageUX={props.languageUX}
      //  medicalfee
      patientInfo={props.patientInfo}
      selectedEncounter={props.selectedEncounter}
      // Invoice items
      invoiceItemByModes={props.invoiceItemByModes}
      invoiceItemByOrders={props.invoiceItemByOrders}
      invoiceItemByItems={props.invoiceItemByItems}
      invoiceItemPackageList={props.invoiceItemPackageList}
      // Orders
      patientOrders={props.patientOrders}
      refundOrders={props.refundOrders}
      // Filters / Status
      medicalFeeFilter={props.medicalFeeFilter}
      patientEncounterLockerStatus={props.patientEncounterLockerStatus}
      encounterSuspension={props.encounterSuspension}
      // Bill / Payment
      BillPaymentSequence={props.BillPaymentSequence}
      billPendingDetail={props.billPendingDetail}
      billReportPrint={props.billReportPrint}
      bilReceiptCodeDetail={props.bilReceiptCodeDetail}
      faxClaimItemList={props.faxClaimItemList}
      taskTrackingList={props.taskTrackingList}
      // Language
      billingPatientLanguage={props.billingPatientLanguage}
      // config
      showMedicalFee={showMedicalFee}
    />
  );

  const menuContentMap: Record<MenuKey, JSX.Element> = {
    [MENU_KEYS.REGISTER_SERVICE]: (
      <CardRegisterService
        setProp={props.setProp}
        runSequence={props.runSequence}
        onEvent={props.onEvent}
        // callback
        onNavigateToSaveService={handleNavigateToSaveService}
        onOpenEnAppointmentSuccess={handleOpenEncounterAppointmentSuccess}
        // data
        selectedCustomer={props.selectedCustomer}
        visitList={props.visitList}
        appointmentList={props.appointmentList}
        packageDetail={props.packageDetail}
        doctorList={props.doctorList}
        // common
        buttonLoadCheck={props.buttonLoadCheck}
        selectedDivision={props.selectedDivision}
        searchedItemListWithKey={props.searchedItemListWithKey}
        masterOptions={props.masterOptions}
        //
        languageUX={props.languageUX}
      />
    ),
    [MENU_KEYS.SAVE_SERVICE_ORDER]: (
      <CardSaveServiceOrder
        setProp={props.setProp}
        runSequence={props.runSequence}
        onEvent={props.onEvent}
        doctorList={props.doctorList}
        selectedCustomer={props.selectedCustomer}
        selectedEncounter={props.selectedEncounter}
        providerList={props.providerList}
        errorMessage={props.errorMessage}
        buttonLoadCheck={props.buttonLoadCheck}
        SaveServiceOrderSequence={props.SaveServiceOrderSequence}
        languageUX={props.languageUX}
        // visit data
        visitList={props.visitList}
        loadingStatus={props.loadingStatus}
      />
    ),
    [MENU_KEYS.SPECIMEN_COLLECTION]: (
      <CardMeLabOrder
        // function
        setProp={props.setProp}
        runSequence={props.runSequence}
        onEvent={props.onEvent}
        // common data
        errorMessage={props.errorMessage}
        successMessage={props.successMessage}
        buttonLoadCheck={props.buttonLoadCheck}
        django={props.django}
        masterOptions={props.masterOptions}
        searchedItemListWithKey={props.searchedItemListWithKey}
        // data
        selectedPatient={props.selectedPatient}
        selectedEncounter={props.selectedEncounter}
        selectedLabOrderWorking={props.selectedLabOrderWorking}
        selectedCentralLabBO={props.selectedCentralLabBO}
        selectedContainer={props.selectedContainer}
        specimenLogs={props.specimenLogs}
        specimenCollectorMasterData={props.specimenCollectorMasterData}
        labOrderQueue={props.labOrderQueue}
        selectedDivision={props.selectedDivision}
        languageUX={props.languageUX}
        // lab report
        LabReportSequence={props.LabReportSequence}
        permissionLabResultConfidential={props.permissionLabResultConfidential}
        patientInfo={props.patientInfo}
        // venipuncture note
        selectedVenipunctureNote={props.selectedVenipunctureNote}
        // config
        isMeBilling={true}
      />
    ),
    [MENU_KEYS.LAB_RESULT]: (
      <CardMeLabResult
        // function
        setProp={props.setProp}
        runSequence={props.runSequence}
        onEvent={props.onEvent}
        // common data
        errorMessage={props.errorMessage}
        buttonLoadCheck={props.buttonLoadCheck}
        django={props.django}
        masterOptions={props.masterOptions}
        // data
        selectedPatient={props.selectedPatient}
        patientInfo={props.selectedPatient}
        languageUX={props.languageUX}
        // lab report
        permissionLabResultConfidential={props.permissionLabResultConfidential}
        centralLabResultList={props.centralLabResultList}
        selectedLabOrderWorking={props.selectedLabOrderWorking}
        searchedItemListWithKey={props.searchedItemListWithKey}
        OPDLabSummarySequence={props.OPDLabSummarySequence}
        // shared lab result
        SharedLabResultSequence={props.SharedLabResultSequence}
      />
    ),
    [MENU_KEYS.PENDING_PAYMENT]: (
      <CardMedicalFee
        showAuthorize={CONFIG.FAX_CLAIM_AUTHORIZE}
        onEvent={props.onEvent}
        setProp={props.setProp as any}
        patientInfo={props.patientInfo}
        selectedEncounter={props.selectedEncounter}
        invoiceItemByModes={props.invoiceItemByModes}
        invoiceItemByOrders={props.invoiceItemByOrders}
        invoiceItemByItems={props.invoiceItemByItems}
        patientOrders={props.patientOrders}
        refundOrders={props.refundOrders}
        medicalFeeFilter={props.medicalFeeFilter}
        patientEncounterLockerStatus={props.patientEncounterLockerStatus}
        showFilter={!CONFIG.FEATURE_DISABLED_UP_TO_VERS["202401"]}
        encounterSuspension={props.encounterSuspension}
        BillPaymentSequence={props.BillPaymentSequence}
        qrCodePayment={props.qrCodePayment}
        loadingStatus={props.loadingStatus}
        errorMessage={props.errorMessage}
        buttonLoadCheck={props.buttonLoadCheck}
        masterOptions={props.masterOptions}
        taskTrackingList={props.taskTrackingList}
        billPendingDetail={props.billPendingDetail}
        bilReceiptCodeDetail={props.bilReceiptCodeDetail}
        billReportPrint={props.billReportPrint}
        faxClaimItemList={props.faxClaimItemList}
        invoiceItemPackageList={props.invoiceItemPackageList}
        billingPatientLanguage={props.billingPatientLanguage}
        selectedDivision={props.selectedDivision}
        languageUX={props.languageUX}
        // config
        isMeBilling={true}
        isHideBillPendingDetail={true}
        isHidePatientOrders={true}
        userProfile={props.userProfile}
      />
    ),
    [MENU_KEYS.PAYMENT_SUMMARY]: (
      <CardPaymentSummary
        setProp={props.setProp}
        runSequence={props.runSequence}
        onEvent={props.onEvent}
        // data
        PaymentSummarySequence={props.PaymentSummarySequence}
        selectedCustomer={props.selectedCustomer}
        // common
        buttonLoadCheck={props.buttonLoadCheck}
        languageUX={props.languageUX}
      />
    ),
    // [MENU_KEYS.ALL_DOCUMENTS]: (
    //   <div style={{ padding: "20px" }}>
    //     <h3>เอกสารทั้งหมด</h3>
    //     <p>Coming soon...</p>
    //   </div>
    // ),
    [MENU_KEYS.PAYMENT_HISTORY]: (
      <CardPaymentHistory
        setProp={props.setProp}
        runSequence={props.runSequence}
        onEvent={props.onEvent}
        // data
        PaymentHistorySequence={props.PaymentHistorySequence}
        selectedCustomer={props.selectedCustomer}
        // common
        masterOptions={props.masterOptions}
        errorMessage={props.errorMessage}
        buttonLoadCheck={props.buttonLoadCheck}
        languageUX={props.languageUX}
      />
    ),
    [MENU_KEYS.PACKAGE]: renderPackageOrder(false),
    [MENU_KEYS.PRODUCT_SERVICE]: renderPackageOrder(true),
    [MENU_KEYS.PRINT_LIST]: (
      <CardPrintList
        printer={props.django?.user?.full_name}
        patientInfo={props.selectedPatient}
        controller={props.printListController}
        patientData={props.selectedPatient}
        patientId={props.selectedEncounter?.patient_id}
        encounterId={props.selectedEncounter?.id}
        encounterNumber={props.selectedEncounter?.number}
        masterData={props.masterData}
        masterOptions={props.masterOptions}
        selectedPatient={props.selectedPatient}
        selectedDivision={props.selectedDivision}
        showPreOrder={true}
        hideLayOut={true}
        languageUX={props.languageUX}
      />
    ),
  };

  return (
    <>
      {/* --- Mod Save Success --- */}
      <Modal open={openSuccessMod} onClose={() => setOpenSuccessMod(false)} size="mini">
        <ModalContent style={{ padding: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Image src={IMAGES.saveSuccess} size="small" />
          </div>

          <Header
            as="h2"
            textAlign="center"
            style={{
              color: "var(--primary-theme-color)",
              marginBottom: "0.5rem",
            }}
          >
            บันทึกสำเร็จ
          </Header>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
              onClick={() => setOpenSuccessMod(false)}
              style={{
                backgroundColor: "var(--primary-theme-color)",
                color: "white",
                minWidth: "120px",
                borderRadius: "8px",
              }}
            >
              ตกลง
            </Button>
          </div>
        </ModalContent>
      </Modal>

      {menuContentMap[props.menu]}
    </>
  );
};

CardMeBillingMenuContent.displayName = "CardMeBillingMenuContent";

export default CardMeBillingMenuContent;
